import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Image,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import Link from '@salesforce/retail-react-app/app/components/link';
import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {ChevronRightCustomizeIcon} from '@amblique/king-living/app/components/icons';
import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';

/**
 * "Not your perfect match?" card for Finished Goods (TSD §7.3 / FSD §7.3).
 *
 * Re-uses the same Contentful fields as the Complex PDP "Customize your own" trigger
 * (`customizeContent`, `customizeImageBackground`) so brand can author one piece of
 * copy that flexes across both surfaces. The only finished-good-specific bit is the
 * destination — clicking "Customize" takes the user to the master/variation-base
 * PDP rather than opening the in-page guided flyout.
 */
export function FinishedGoodNotPerfectMatchCard({customizeContent, customizeImageUrl, customizePdpHref}) {
  const styles = useMultiStyleConfig('FinishedGoodView', {});

  // Don't render if we have neither Contentful copy nor a destination — the card
  // would just be empty space with a dead link. Brand-managed visibility.
  if (!customizeContent && !customizePdpHref) return null;

  const linkContent = (
    <Box className="finished-good-not-perfect-match__card" {...styles.notPerfectMatchCard}>
      <Flex className="finished-good-not-perfect-match__row" {...styles.notPerfectMatchRow}>
        <Box className="finished-good-not-perfect-match__content" {...styles.notPerfectMatchContent}>
          {customizeContent ? (
            <Box {...styles.notPerfectMatchTitle}>
              <RichTextContent content={customizeContent} {...styles.notPerfectMatchDescription} />
            </Box>
          ) : (
            <Text {...styles.notPerfectMatchDescription}>
              <FormattedMessage
                defaultMessage="Not your perfect match? Customize this design to suit your space."
                id="finished_good.not_perfect_match.fallback"
              />
            </Text>
          )}
          {customizePdpHref && (
            <Flex className="finished-good-not-perfect-match__link-row" {...styles.notPerfectMatchLinkRow}>
              <Text {...styles.notPerfectMatchLink}>
                <FormattedMessage
                  defaultMessage="Customise"
                  id="product_view.variation.customize_button"
                />
              </Text>
              <ChevronRightCustomizeIcon
                aria-hidden
                boxSize="12px"
                color="brandColors.primaryDarkLinen"
                flexShrink={0}
                minH="unset"
                minW="unset"
              />
            </Flex>
          )}
        </Box>

        {customizeImageUrl ? (
          <Box
            className="finished-good-not-perfect-match__image-wrapper"
            {...styles.notPerfectMatchImageWrapper}
          >
            <Image
              alt=""
              role="presentation"
              src={customizeImageUrl}
              {...styles.notPerfectMatchImage}
            />
          </Box>
        ) : null}
      </Flex>
    </Box>
  );

  if (customizePdpHref) {
    return (
      <Box className="finished-good-not-perfect-match" {...styles.notPerfectMatchRoot}>
        <Link
          borderRadius="4px"
          className="finished-good-not-perfect-match__link"
          data-testid="finished-good-customize-link"
          display="block"
          textDecoration="none"
          to={customizePdpHref}
          _hover={{textDecoration: 'none'}}
          {...A11Y_FOCUS_VISIBLE}
        >
          {linkContent}
        </Link>
      </Box>
    );
  }

  return (
    <Box className="finished-good-not-perfect-match" {...styles.notPerfectMatchRoot}>
      {linkContent}
    </Box>
  );
}

FinishedGoodNotPerfectMatchCard.displayName = 'FinishedGoodNotPerfectMatchCard';

FinishedGoodNotPerfectMatchCard.propTypes = {
  customizeContent: PropTypes.any,
  customizeImageUrl: PropTypes.string,
  customizePdpHref: PropTypes.string,
};
